/**
 * AgentsPage Component
 *
 * This component serves as the main page for displaying and managing agents.
 * It includes features for:
 * - Displaying a paginated list of agents in a table format
 * - Searching and filtering agents by various criteria
 * - Handling pagination for large datasets
 *
 * The component uses the AgentsService to fetch data from the backend API
 * and manages the state for agents, filtering, pagination, and loading states.
 */

import { useEffect, useState } from 'react';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import UnifiedTopBar from '@frontend/Components/TopBar/UnifiedTopBar';
import { createListViewTemplate } from '@frontend/Components/TopBar/templates';
import './AgentsPage.css';
import AgentsTable from './Components/AgentsTable/agentsTable';
import AgentsService from '../AppSettings/services/AgentsService';
import ExportAgents from './Components/Dialog/ExportAgents';
import { isLegalEnvironment } from '../AppSettings/environmentConfig';
import useActiveEmployeeId from '../AppSettings/hooks/useActiveEmployeeId';
import MicrosoftHttpService from '../AppSettings/services/MicrosoftHttpService';
import { chunkArray } from '../utils/arrayUtils';
import { ContactToSync, ImportResult } from '../Models/Microsoft/ContactModels';

/**
 * Mapping of display column names to their corresponding data field names
 * Used for filtering and displaying agent data in the table
 */
const columns = {
  'Agent ID': 'AgentID',
  Agents: '', // Empty string for Agents as it combines FirstName and LastName
  Company: 'Company',
  Email: 'Email',
  Phone: 'Phone',
  Address: 'Address',
};

const AgentsPage = () => {
  // State management for the component
  const [agents, setAgents] = useState<any[]>([]); // Store all agents from API
  const [filteredAgents, setFilteredAgents] = useState<any[]>([]); // Store filtered agents for display
  const [page, setPage] = useState<number>(1); // Current page number
  const [pageLimit, setPageLimit] = useState<number>(1); // Total number of pages
  const [pageSize, setPageSize] = useState<number>(7); // Items per page
  const [loading, setLoading] = useState<boolean>(true); // Loading state for API calls
  const [error, setError] = useState<string | null>(null); // Error state for API calls

  // Outlook sync state
  const [showOutlookModal, setShowOutlookModal] = useState<boolean>(false);
  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const employeeId = useActiveEmployeeId();

  /**
   * useEffect hook to fetch agents data when component mounts
   * Makes an API call to get all agents and updates the state accordingly
   */
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        // Fetch agents data from the API
        const data = await AgentsService.getAgents();
        setAgents(data);
        setFilteredAgents(data); // Initialize filtered list with all agents
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setLoading(false);
      }
    };

    fetchAgents();
  }, []); // Empty dependency array means this effect runs once on mount

  /**
   * Update page limit when filtered agents or page size changes
   */
  useEffect(() => {
    if (filteredAgents) {
      setPageLimit(Math.max(1, Math.ceil(filteredAgents.length / pageSize)));
    }
  }, [filteredAgents, pageSize]);

  /**
   * Handler for search functionality
   * Filters agents based on search text and selected filter type
   *
   * @param {string} search - The search text to filter agents
   * @param {string} filter - The column to filter by (e.g., "Agents", "Email", etc.)
   */
  const handleSearch = (search: string, filter?: string | null) => {
    if (!filter || !search) {
      // If no filter or search term, show all data
      setFilteredAgents(agents);
      setPageLimit(Math.max(1, Math.ceil(agents.length / pageSize)));
      setPage(1);
      return;
    }

    let filtered;
    if (filter === 'Agents') {
      // Special case: search in FirstName, LastName, and ContactName fields
      filtered = agents.filter((entry) => {
        const firstName = entry.FirstName ? entry.FirstName.toLowerCase() : '';
        const lastName = entry.LastName ? entry.LastName.toLowerCase() : '';
        const contactName = entry.ContactName
          ? entry.ContactName.toLowerCase()
          : '';
        const searchLower = search.toLowerCase();

        return (
          firstName.includes(searchLower) ||
          lastName.includes(searchLower) ||
          contactName.includes(searchLower)
        );
      });
    } else {
      // Search in the specified column using the columns mapping
      const fieldKey = columns[filter as keyof typeof columns];
      filtered = agents.filter((entry) =>
        (entry as any)[fieldKey]?.toLowerCase().includes(search.toLowerCase())
      );
    }
    setFilteredAgents(filtered);
    setPageLimit(Math.max(1, Math.ceil(filtered.length / pageSize)));
    setPage(1); // Reset to first page after search
  };

  /**
   * Handler for pagination
   * Updates the current page number when user navigates through pages
   *
   * @param {number} newPage - The new page number to set
   */
  const handlePagination = (newPage: number) => {
    setPage(newPage);
  };

  /**
   * Handler for page size changes
   * Updates the page size and resets to first page
   *
   * @param {number} newPageSize - The new page size to set
   */
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page when changing page size
  };

  /**
   * Handler for refreshing agents data
   * Refetches agents from the API and updates the state
   */
  const handleAgentsRefresh = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await AgentsService.getAgents();
      setAgents(data);
      setFilteredAgents(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handler for Outlook modal open
   * Shows the sync confirmation modal
   */
  const handleOutlookClick = () => {
    setShowOutlookModal(true);
    setImportResult(null);
  };

  /**
   * Handler for starting the actual sync process
   * Automatically syncs all Outlook contacts to the agents table
   */
  const handleStartSync = async () => {
    setIsLoadingContacts(true);
    setImportResult(null);

    try {
      // Check if user is authenticated with MSAL
      if (!employeeId) {
        throw new Error('Please sign in to access Outlook contacts');
      }

      // Create Microsoft Graph service instance with contacts scope
      const microsoftService = new MicrosoftHttpService([
        'https://graph.microsoft.com/Contacts.Read',
      ]);

      // Fetch contacts from Microsoft Graph using the service
      const contactsData = await microsoftService.getContacts();
      const outlookContacts = contactsData.value || [];

      // Automatically sync all contacts
      await syncAllContacts(outlookContacts);
    } catch (error) {
      console.error('Error syncing Outlook contacts:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setImportResult({
        message: `Failed to sync Outlook contacts: ${errorMessage}`,
        imported_count: 0,
        updated_count: 0,
        skipped_count: 0,
        total_processed: 0,
        errors: [errorMessage],
      });
    } finally {
      setIsLoadingContacts(false);
    }
  };

  /**
   * Automatically sync all Outlook contacts to agents table
   */
  const syncAllContacts = async (outlookContacts: any[]) => {
    try {
      const contactsToSync: ContactToSync[] = outlookContacts.map((contact: any) => ({
        firstName: contact.givenName || '',
        lastName: contact.surname || '',
        email: contact.emailAddresses?.[0]?.address || '',
        company: contact.companyName || '',
        phone:
          contact.mobilePhone ||
          contact.businessPhones?.[0] ||
          contact.homePhones?.[0] ||
          '',
        address: contact.addresses?.[0]
          ? `${contact.addresses[0].street || ''} ${
              contact.addresses[0].city || ''
            } ${contact.addresses[0].state || ''} ${
              contact.addresses[0].postalCode || ''
            }`.trim()
          : '',
        title: contact.jobTitle || '',
        notes: contact.personalNotes || contact.notes || '',
      }));

      console.log('Syncing contacts:', contactsToSync);

      // Function to sync a chunk of contacts
      const chunkedArray = chunkArray(contactsToSync);

      const responseTotal: ImportResult = {
        message: '',
        imported_count: 0,
        updated_count: 0,
        skipped_count: 0,
        total_processed: 0,
        errors: [],
      };

      for (const chunk of chunkedArray) {
        const response = await AgentsService.syncOutlookContacts({
          contacts: chunk as ContactToSync[],
        });

        responseTotal.message = response.message + '/n';
        responseTotal.imported_count += response.imported_count;
        responseTotal.updated_count += response.updated_count;
        responseTotal.skipped_count += response.skipped_count;
        responseTotal.total_processed += response.total_processed;
        responseTotal.errors = responseTotal.errors.concat(response.errors || []);
      }

      console.log('Sync result:', responseTotal);
      setImportResult(responseTotal);

      // Refresh agents list
      handleAgentsRefresh();
    } catch (error) {
      console.error('Error syncing contacts:', error);
      throw error;
    }
  };

  /**
   * Handler for closing the Outlook sync modal
   */
  const closeOutlookModal = () => {
    setShowOutlookModal(false);
    setImportResult(null);
    setIsLoadingContacts(false);
  };

  const searchColumns = {
    'Agent ID': 'Agent ID',
    Agents: 'Agents',
    Company: 'Company',
    Email: 'Email',
    Phone: 'Phone',
    Address: 'Address',
  };

  const config = createListViewTemplate({
    search: {
      columns: searchColumns,
      defaultSelectedColumn: 'Agents',
      onSearch: handleSearch,
    },
    exportModal: isLegalEnvironment ? {
      modalId: 'agents-export',
      modalConfig: {
        component: ExportAgents,
        config: {},
      },
    } : undefined,
    rightExtras: [
      {
        type: 'button',
        icon: faRotate,
        onClick: handleOutlookClick,
        ariaLabel: 'Sync Outlook Contacts',
      },
    ],
    pageContext: {
      route: 'agents',
    },
  });

  // Show loading state while fetching data
  if (loading) return <p>Loading...</p>;
  // Show error message if API call fails
  if (error) return <p>Error: {error}</p>;

  // Render the main component
  return (
    <div className="agentsPage">
      <UnifiedTopBar {...config} />
      {/* Table component to display agent data with pagination */}
      <AgentsTable
        data={filteredAgents.slice((page - 1) * pageSize, page * pageSize)}
        onAgentDeleted={handleAgentsRefresh}
        pageSize={pageSize}
        pagination={{
          currPage: page,
          onPageChange: handlePagination,
          pageLimit: pageLimit,
          pageSize: pageSize,
          onPageSizeChange: handlePageSizeChange,
        }}
      />

      {/* Outlook sync modal with overlay */}
      {showOutlookModal && (
        <div className="modal" onClick={(event) => {
          if (event.target === event.currentTarget) {
            closeOutlookModal();
          }
        }}>
          <div>
            <div className="agent-popup-outlook-import-modal">
              <div className="agent-popup-modal-header">
                <h2>Outlook Contact Sync</h2>
                <button
                  className="agent-popup-close-button"
                  onClick={closeOutlookModal}
                >
                  ×
                </button>
              </div>

              <div className="agent-popup-modal-body">
                {isLoadingContacts ? (
                  <div className="agent-popup-loading-state">
                    <div className="sync-spinner"></div>
                    <p>Syncing your Outlook contacts...</p>
                    <p style={{ fontSize: '0.9em', color: '#666' }}>
                      This may take a moment depending on the number of
                      contacts.
                    </p>
                  </div>
                ) : importResult ? (
                  <div className="agent-popup-import-result">
                    <h3>✅ Sync Complete!</h3>
                    <p>{importResult.message}</p>
                    <div className="agent-popup-import-stats">
                      <div className="stat-item">
                        <span className="stat-number">
                          {importResult.imported_count}
                        </span>
                        <span className="stat-label">New Contacts</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">
                          {importResult.updated_count}
                        </span>
                        <span className="stat-label">Updated</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">
                          {importResult.skipped_count}
                        </span>
                        <span className="stat-label">Skipped</span>
                      </div>
                    </div>
                    {importResult.errors && importResult.errors.length > 0 && (
                      <div className="sync-errors">
                        <h4>Note:</h4>
                        <ul>
                          {importResult.errors
                            .slice(0, 5)
                            .map((error, index) => (
                              <li key={index}>{error}</li>
                            ))}
                          {importResult.errors.length > 5 && (
                            <li>
                              ... and {importResult.errors.length - 5} more
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                    <button
                      onClick={closeOutlookModal}
                      className="agent-popup-done-button"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="agent-popup-sync-info">
                    <div className="sync-info-content">
                      <h3>Ready to Sync Outlook Contacts</h3>
                      <p>
                        This will automatically sync all your Outlook contacts
                        to the agents table.
                      </p>
                      <ul>
                        <li>✅ New contacts will be added automatically</li>
                        <li>
                          ✅ Existing contacts will be updated with latest info
                        </li>
                        <li>✅ Notes from Outlook will be synced</li>
                        <li>✅ No duplicates will be created</li>
                      </ul>
                    </div>

                    <div className="agent-popup-modal-actions">
                      <button
                        onClick={closeOutlookModal}
                        className="agent-popup-cancel-button"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleStartSync}
                        disabled={isLoadingContacts}
                        className="agent-popup-import-button"
                      >
                        {isLoadingContacts ? 'Syncing...' : 'Start Sync'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentsPage;
